import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient } from '@wix/sdk-types';
import {
  ListDefaultTaxGroupsByAppIdsResponse,
  ListDefaultTaxGroupsByAppIdsResponseNonNullableFields,
  ListDefaultTaxGroupsResponse,
  ListDefaultTaxGroupsResponseNonNullableFields,
  TaxGroup,
  TaxGroupCreatedEnvelope,
  TaxGroupDeletedEnvelope,
  TaxGroupNonNullableFields,
  TaxGroupUpdatedEnvelope,
  TaxGroupsQueryBuilder,
  UpdateTaxGroup,
  createTaxGroup as universalCreateTaxGroup,
  deleteTaxGroup as universalDeleteTaxGroup,
  getTaxGroup as universalGetTaxGroup,
  listDefaultTaxGroups as universalListDefaultTaxGroups,
  listDefaultTaxGroupsByAppIds as universalListDefaultTaxGroupsByAppIds,
  queryTaxGroups as universalQueryTaxGroups,
  updateTaxGroup as universalUpdateTaxGroup,
} from './billing-v1-tax-group-tax-groups.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createTaxGroup(
  httpClient: HttpClient
): CreateTaxGroupSignature {
  return (taxGroup: TaxGroup) =>
    universalCreateTaxGroup(
      taxGroup,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateTaxGroupSignature {
  /**
   * Creates a tax group.
   *
   * Call Stores Update Product to add the `taxGroupId` to specific products to categorize as a group based on distinct tax treatment.
   * Wix uses tax groups to calculate tax.
   *
   * In addition to tax groups you create, default tax groups are already included in all Wix catalogs.
   * Call List Default Tax Groups to retrieve them. You can also use the Tax Groups Integration service plugin (REST only)
   * to create new default tax groups that can be applied directly to an entire catalog of products.
   * @param - Tax group to create.
   * @returns Created tax group.
   */
  (taxGroup: TaxGroup): Promise<TaxGroup & TaxGroupNonNullableFields>;
}

export function getTaxGroup(httpClient: HttpClient): GetTaxGroupSignature {
  return (taxGroupId: string) =>
    universalGetTaxGroup(
      taxGroupId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTaxGroupSignature {
  /**
   * Retrieves a tax group.
   * @param - ID of the tax group to retrieve.
   * @returns Retrieved tax group.
   */
  (taxGroupId: string): Promise<TaxGroup & TaxGroupNonNullableFields>;
}

export function updateTaxGroup(
  httpClient: HttpClient
): UpdateTaxGroupSignature {
  return (_id: string | null, taxGroup: UpdateTaxGroup) =>
    universalUpdateTaxGroup(
      _id,
      taxGroup,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTaxGroupSignature {
  /**
   * Updates a tax group.
   *
   * Each time the tax group is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the tax group.
   * This ensures you're working with the latest tax group and prevents
   * unintended overwrites.
   * @param - Tax group ID.
   * @param - Tax group info.
   * @returns Updated tax group.
   */
  (_id: string | null, taxGroup: UpdateTaxGroup): Promise<
    TaxGroup & TaxGroupNonNullableFields
  >;
}

export function deleteTaxGroup(
  httpClient: HttpClient
): DeleteTaxGroupSignature {
  return (taxGroupId: string) =>
    universalDeleteTaxGroup(
      taxGroupId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTaxGroupSignature {
  /**
   * Deletes a tax group.
   *
   * If a tax group is deleted but the `taxGroupId` is still assigned to a product (see Stores Products API) then the default tax group is used to calculate tax.
   * @param - ID of the tax group to delete.
   */
  (taxGroupId: string): Promise<void>;
}

export function queryTaxGroups(
  httpClient: HttpClient
): QueryTaxGroupsSignature {
  return () =>
    universalQueryTaxGroups(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryTaxGroupsSignature {
  /**
   * Creates a query to retrieve a list of tax groups.
   *
   * The `queryTaxGroups()` function builds a query to retrieve a list of tax groups and returns a `TaxGroupsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `TaxGroupsQueryBuilder` functions onto the query. `TaxGroupsQueryBuilder` functions enable you to sort, filter, and control the results that `queryTaxGroups()` returns.
   *
   * `queryTaxGroups()` runs with the following `TaxGroupsQueryBuilder` default that you can override:
   * + `ascending("_id")`
   *
   * The functions that are chained to `queryTaxGroups()` are applied in the order they are called. For example, if you apply `ascending("name")` and then `ascending("_createdDate")`, the results are sorted first by the `"name"`, and then, if there are multiple results with the same `"name"`, the items are sorted by `"_createdDate"`.
   *
   * The following `TaxGroupsQueryBuilder` functions are supported for the `queryTaxGroups()` function. For a full description of the tax group object, see the object returned for the `items` property in `TaxGroupsQueryResult`.
   */
  (): TaxGroupsQueryBuilder;
}

export function listDefaultTaxGroups(
  httpClient: HttpClient
): ListDefaultTaxGroupsSignature {
  return () =>
    universalListDefaultTaxGroups(
      // @ts-ignore
      { httpClient }
    );
}

interface ListDefaultTaxGroupsSignature {
  /**
   * Retrieves a list of default tax groups.
   *
   * The default tax groups for a site are inherited by the apps installed on the site.
   * For example, the Wix Stores app includes a `"Products"` tax group by default.
   *
   * Add additional default tax groups with the Tax Groups Integration service plugin (REST only).
   */
  (): Promise<
    ListDefaultTaxGroupsResponse & ListDefaultTaxGroupsResponseNonNullableFields
  >;
}

export function listDefaultTaxGroupsByAppIds(
  httpClient: HttpClient
): ListDefaultTaxGroupsByAppIdsSignature {
  return (appIds: string[]) =>
    universalListDefaultTaxGroupsByAppIds(
      appIds,
      // @ts-ignore
      { httpClient }
    );
}

interface ListDefaultTaxGroupsByAppIdsSignature {
  /**
   * Retrieves default tax groups for specific apps.
   * @param - App IDs to retrieve default tax groups for.
   */
  (appIds: string[]): Promise<
    ListDefaultTaxGroupsByAppIdsResponse &
      ListDefaultTaxGroupsByAppIdsResponseNonNullableFields
  >;
}

export const onTaxGroupCreated = EventDefinition(
  'wix.billing.v1.tax_group_created',
  true,
  (event: TaxGroupCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TaxGroupCreatedEnvelope>();
export const onTaxGroupDeleted = EventDefinition(
  'wix.billing.v1.tax_group_deleted',
  true,
  (event: TaxGroupDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TaxGroupDeletedEnvelope>();
export const onTaxGroupUpdated = EventDefinition(
  'wix.billing.v1.tax_group_updated',
  true,
  (event: TaxGroupUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<TaxGroupUpdatedEnvelope>();

export {
  ActionEvent,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  CreateTaxGroupRequest,
  CreateTaxGroupResponse,
  CreateTaxGroupResponseNonNullableFields,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeleteTaxGroupRequest,
  DeleteTaxGroupResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetTaxGroupRequest,
  GetTaxGroupResponse,
  GetTaxGroupResponseNonNullableFields,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  ListDefaultTaxGroupsByAppIdsRequest,
  ListDefaultTaxGroupsByAppIdsResponse,
  ListDefaultTaxGroupsByAppIdsResponseNonNullableFields,
  ListDefaultTaxGroupsByAppIdsResult,
  ListDefaultTaxGroupsRequest,
  ListDefaultTaxGroupsResponse,
  ListDefaultTaxGroupsResponseNonNullableFields,
  MessageEnvelope,
  Paging,
  QueryTaxGroupsRequest,
  QueryTaxGroupsResponse,
  QueryTaxGroupsResponseNonNullableFields,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  SortOrder,
  Sorting,
  TaxGroup,
  TaxGroupCreatedEnvelope,
  TaxGroupDeletedEnvelope,
  TaxGroupNonNullableFields,
  TaxGroupUpdatedEnvelope,
  TaxGroupsQueryBuilder,
  TaxGroupsQueryResult,
  UpdateTaxGroup,
  UpdateTaxGroupRequest,
  UpdateTaxGroupResponse,
  UpdateTaxGroupResponseNonNullableFields,
  WebhookIdentityType,
} from './billing-v1-tax-group-tax-groups.universal.js';
